import Link from "next/link";
import { Row, Tooltip, theme } from "antd";
import { EntityTypes } from "@vzbl/integrations";
import { SearchState } from "src/search/types";
import { formatTime } from "src/utils/date";
import { useApplicationContext } from "src/hooks/useApplication";
import { HeatMap } from "pages/[orgId]/explore/lib/HeatMap";
import { UngroupedHelper } from "pages/[orgId]/explore/ResponseCanvas";
import { useExplore } from "pages/[orgId]/explore/useExploreContext";
import { useSearch } from "src/search/useSearchContext";
import {
  ContentViews,
  SerializableView,
  serializeContentView,
} from "pages/[orgId]/explore/lib/useContentView";
import { SerializableDoc } from "pages/[orgId]/explore/lib/useExploreDocument";
import { SerializableTabs } from "pages/[orgId]/explore/lib/useTabs";
import { generateWorkTabs } from "../components/worktabs";
import { TabbedWork, TabbedWorkItems } from "../components/TabbedWork";
import { Spacer } from "../components/Spacer";
import { exploreFromSearchState } from "../workstreams/common";

export function Flow(props: { entity: any }) {
  const { token } = theme.useToken();
  const orgId = useApplicationContext().org_id!;
  const workTabsItems: TabbedWorkItems[] = generateWorkTabs(
    orgId,
    props.entity.properties.uuid,
    [
      { entityTypes: EntityTypes.Person },
      { entityTypes: EntityTypes.CalendarEntry, label: "Calendar Entries" },
      { entityTypes: EntityTypes.Message },
    ]
  );
  const personName = props.entity.properties.Person.value;
  const initial: {
    view?: SerializableView;
    doc?: SerializableDoc;
    tabs?: SerializableTabs;
    entity?: string;
  } = {
    view: {
      value: ContentViews.heatMap,
      config: {
        labels: true,
        values: true,
        hex: false,
        invertAxes: false,
      },
    },
  };
  const searchState: SearchState = {
    orgId,
    select: [EntityTypes.Event],
    filters: [
      {
        key: `${EntityTypes.Event}.Actor`,
        attribute: "Actor",
        target: EntityTypes.Event,
        comparison: {
          IN: [personName],
        },
      },
    ],
    search: "",
    startDate: props.entity.properties.dayStart,
    endDate: props.entity.properties.dayEnd,
    groupBys: [
      { target: EntityTypes.Event, attribute: "Hour" },
      { target: EntityTypes.Event, attribute: "EntityType" },
    ],
    aggregations: [
      {
        target: EntityTypes.Event,
        attribute: "uuid",
        reducer: "count",
      },
    ],
  };
  const searchContext = useSearch(searchState);
  const exploreContext = useExplore(searchContext, initial);

  return (
    <>
      <Row
        className="section-header"
        align="middle"
        style={{ backgroundColor: `${token.colorBgLayout}`, marginTop: 16 }}
      >
        {personName}&apos;s Events by Hour
      </Row>
      <Spacer height={10} />
      <Tooltip title="View in Explore" placement="bottom">
        <Link
          target="_blank"
          href={exploreFromSearchState(
            searchState,
            `${personName} Flow on ${formatTime(
              props.entity.properties.Date,
              "MMM d, YYYY"
            )}`,
            serializeContentView({
              value: ContentViews.heatMap,
              config: {},
            })
          )}
        >
          <div style={{ height: 300 }}>
            <HeatMap
              context={exploreContext}
              help={<UngroupedHelper context={exploreContext} />}
            />
          </div>
        </Link>
      </Tooltip>

      <Spacer />
      <TabbedWork title="Work" items={workTabsItems} />
    </>
  );
}
